import type { Employee, RequestField, TimeOff, User } from "../lib/types";
import { TIME_OFF_LABEL } from "../lib/types";
import { Button } from "./ui/Button";
import { Modal } from "./ui/Modal";

// Resolve a reviewer's user id to a display name. Falls back to the
// employees catalogue so a reviewer who's also a deactivated user
// still renders by name. "Unknown" is the last resort.
function reviewerLabel(
  userId: string | null,
  usersById: Record<string, User>,
  empById: Record<string, Employee>,
): string {
  if (!userId) return "Unknown";
  const u = usersById[userId];
  if (u) {
    const emp = u.employeeId ? empById[u.employeeId] : undefined;
    if (emp) return `${emp.lastName}, ${emp.firstName}`;
    return u.username || u.email || "Unknown";
  }
  return "Unknown";
}

function fmtWhen(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

// Read-only review modal opened from a row in the request list.
//
// Owner / L1 / L2 see Approve / Deny in the footer for active
// requests; everyone sees the typed-in fields, the note, and the
// status. Once a request is decided, the footer swaps to a Print
// action that exports just this one form (the page-level Print
// still exports the entire completed list).
//
// Same layout shape as the printable form so what the supervisor
// reads on screen matches what comes out on paper.
export function RequestDetailModal({
  open, request, employee, fields, kindLabel,
  canReview, canFinalize, onReview, onPrint, onClose,
  usersById = {}, empById = {},
}: {
  open: boolean;
  request: TimeOff | null;
  employee: Employee | undefined;
  fields: RequestField[];
  kindLabel: "Training" | "Time off";
  // canReview = "viewer is L2+" (can take any approval action).
  // canFinalize = "viewer is L1 or Owner" — needed to flip a
  // training request from l2_approved to approved.
  canReview: boolean;
  canFinalize: boolean;
  onReview: (id: string, approved: boolean) => void;
  onPrint: (id: string) => void;
  onClose: () => void;
  // Looked up to resolve reviewer ids to names. Both optional so a
  // caller that hasn't loaded users yet still renders (reviewer
  // names just say "Unknown").
  usersById?: Record<string, User>;
  empById?: Record<string, Employee>;
}) {
  if (!request) return null;

  const filled = fields
    .map((f) => ({ f, v: request.metadata?.[f.key] }))
    .filter(({ v, f }) => {
      if (f.fieldType === "checkbox") return v === true;
      return v !== undefined && v !== null && v !== "";
    });

  // "Pending" covers both initial requests and L2-partial-approved
  // training requests. Both surface review buttons in the modal,
  // but who can press which one depends on the viewer's level —
  // see the canApproveNow / canDenyNow gating below.
  const isPending = request.status === "requested"
    || request.status === "l2_approved";
  const isTraining = kindLabel === "Training";
  const isFinalRequired = isTraining && request.status === "l2_approved";
  // Approve action: any L2+ can flip "requested" → l2_approved
  // (training) or "approved" (non-training). Only L1+ can flip an
  // already-l2_approved training row to fully approved.
  const canApproveNow = canReview && (!isFinalRequired || canFinalize);
  // Deny is always immediate from any review-capable level.
  const canDenyNow = canReview && isPending;
  // Button labels reflect the two-stage flow so an L2 doesn't
  // think their tap is the final say-so on a training request.
  const approveLabel = isTraining && !canFinalize && request.status === "requested"
    ? "Approve (pending L1)"
    : "Approve";

  const officerLabel = employee
    ? `${employee.lastName}, ${employee.firstName}`
      + (employee.rank ? ` · ${employee.rank}` : "")
      + (employee.badgeNumber ? ` · #${employee.badgeNumber}` : "")
    : request.employeeId;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${kindLabel} request`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Close</Button>
          {canDenyNow && (
            <Button
              variant="ghost"
              onClick={() => { onReview(request.id, false); onClose(); }}
            >
              Deny
            </Button>
          )}
          {canApproveNow && (
            <Button
              onClick={() => { onReview(request.id, true); onClose(); }}
            >
              {approveLabel}
            </Button>
          )}
          {!isPending && (
            <Button onClick={() => onPrint(request.id)}>
              Print / PDF
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <StatusBanner status={request.status} />

        <DetailRow label="Officer">{officerLabel}</DetailRow>
        <DetailRow label="Submitted">{request.createdAt.slice(0, 10)}</DetailRow>
        <DetailRow label="Type">
          {kindLabel === "Training" ? "Training" : TIME_OFF_LABEL[request.type]}
        </DetailRow>
        <DetailRow label="Dates">
          {request.startDate} → {request.endDate}
        </DetailRow>

        <ReviewerBlock
          request={request}
          isTraining={isTraining}
          usersById={usersById}
          empById={empById}
        />

        {filled.length > 0 && (
          <section className="pt-2 border-t border-slate-200">
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Details
            </div>
            <dl className="grid grid-cols-1 gap-y-1">
              {filled.map(({ f, v }) => (
                <DetailRow key={f.id} label={f.label}>
                  {f.fieldType === "checkbox" ? "Yes" : String(v)}
                </DetailRow>
              ))}
            </dl>
          </section>
        )}

        {request.note && (
          <section className="pt-2 border-t border-slate-200">
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Note
            </div>
            <p className="whitespace-pre-wrap text-slate-700">{request.note}</p>
          </section>
        )}
      </div>
    </Modal>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <dt className="w-32 shrink-0 text-[11px] font-semibold uppercase tracking-wider text-slate-500 pt-0.5">
        {label}
      </dt>
      <dd className="flex-1 text-slate-800">{children}</dd>
    </div>
  );
}

// Reviewer attribution rows. Training surfaces both the L2 partial
// approver and the final L1/Owner approver — even when L1 acted
// directly on a `requested` row, the L2 slot still renders as
// "Skipped — Level 1 approved directly" so the audit story is
// complete. Time-off only shows the single reviewer.
function ReviewerBlock({
  request, isTraining, usersById, empById,
}: {
  request: TimeOff;
  isTraining: boolean;
  usersById: Record<string, User>;
  empById: Record<string, Employee>;
}) {
  const decided = request.status === "approved" || request.status === "denied";
  const partial = request.status === "l2_approved";
  if (!decided && !partial) return null;

  const verb = request.status === "denied" ? "Denied" : "Approved";

  if (isTraining) {
    const l2Name = request.l2ApprovedBy
      ? reviewerLabel(request.l2ApprovedBy, usersById, empById)
      : null;
    const l2When = request.l2ApprovedAt ? fmtWhen(request.l2ApprovedAt) : "";
    const finalName = request.reviewedBy
      ? reviewerLabel(request.reviewedBy, usersById, empById)
      : null;
    const finalWhen = request.reviewedAt ? fmtWhen(request.reviewedAt) : "";

    return (
      <>
        <DetailRow label="L2 approval">
          {l2Name
            ? <>{l2Name}{l2When && <span className="text-slate-500"> · {l2When}</span>}</>
            : request.status === "approved"
              ? <span className="text-slate-500 italic">Skipped — Level 1 approved directly</span>
              : <span className="text-slate-500 italic">Not yet</span>}
        </DetailRow>
        {decided && (
          <DetailRow label={`Final ${verb.toLowerCase()}`}>
            {finalName ?? "Unknown"}
            {finalWhen && <span className="text-slate-500"> · {finalWhen}</span>}
          </DetailRow>
        )}
      </>
    );
  }

  if (!decided) return null;
  const name = request.reviewedBy
    ? reviewerLabel(request.reviewedBy, usersById, empById)
    : "Unknown";
  const when = request.reviewedAt ? fmtWhen(request.reviewedAt) : "";
  return (
    <DetailRow label={`${verb} by`}>
      {name}
      {when && <span className="text-slate-500"> · {when}</span>}
    </DetailRow>
  );
}

function StatusBanner({ status }: { status: TimeOff["status"] }) {
  const config = status === "approved"
    ? { label: "APPROVED", cls: "bg-green-50 border-green-300 text-green-800" }
    : status === "denied"
    ? { label: "DENIED",   cls: "bg-red-50 border-red-300 text-red-800" }
    : status === "l2_approved"
    ? { label: "L2 APPROVED — AWAITING L1/OWNER", cls: "bg-sky-50 border-sky-300 text-sky-800" }
    : { label: "AWAITING REVIEW", cls: "bg-amber-50 border-amber-300 text-amber-800" };
  return (
    <div className={`px-3 py-1.5 text-xs font-semibold tracking-wider rounded border ${config.cls}`}>
      Status: {config.label}
    </div>
  );
}
